import {Button, Grid, useTheme} from "@mui/material";
import React, {MouseEventHandler, useContext, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import MainDefinitionComponent from "./sections/MainDefinitionComponent";
import {useRecoilState} from "recoil";
import {rateSelectListAtom} from "../../../state/ratesRecoil";
import {unitsEnumAtom} from "../../../state/rentalPeriodsRecoil";
import {currencyAtom} from "../../../state/constantsRecoil";
import OtherDefinitionComponent from "./sections/OtherDefinitionComponent";
import ReferenceTariffComponent from "./sections/ReferenceTariffComponent";
import AdvancedBookingComponent from "./sections/AdvancedBookingComponent";
import {FormRateModel, RateEntityResponse, RateSelectFE,} from "../../../api/types/rate";
import {commercialCategoryListAtom} from "../../../state/commercialCategoriesRecoil";
import CustomBanner from "../../../common/components/CustomBanner";
import {useFormContext} from "react-hook-form";
import {SelectElementModelFE} from "../../../api/types/generic";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE,} from "../../../api/types/commercialCategory";
import {fromGenericBeToGenericFe} from "../../../utils";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";
import dayjs from "dayjs";

const RateBaseDefinition = ({
                                rateInUse,
                                readOnly,
                                handleSubmit,
                                providers,
                                rate,
                            }: {
    rateInUse: boolean;
    readOnly: boolean;
    handleSubmit: MouseEventHandler<HTMLButtonElement>;
    providers: SelectElementModelFE[];
    rate: RateEntityResponse | null;
}) => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["tranlsation", "pricing"]);

    // Use Navigate
    const navigate = useNavigate();

    // Use Recoil
    const [commercialCategoryList, setCommercialCategoryList] = useRecoilState(
        commercialCategoryListAtom
    );
    const [commercialSegmentList] = useRecoilState(commercialSegmentListAtom);
    const [unitsEnum] = useRecoilState(unitsEnumAtom);
    const [currencyList] = useRecoilState(currencyAtom);
    const [rateSelectList] = useRecoilState(rateSelectListAtom);

    // Use Repository Hooks
    const {
        getCommercialCategories,
        getCommercialCategoriesByCommercialSegmentId,
    } = useCommercialCategoryRepository();

    // Use Form Context
    const form = useFormContext<FormRateModel>();
    const watchAllFields = form.watch();

    // Use Context
    const {
        globalData: {user},
    } = useContext(GlobalContext);

    // Use Effect
    useEffect(() => {
        const getContent = async () => {
            if (watchAllFields?.mainDefinition?.commercialSegment) {
                const commercialCategoriesData =
                    await getCommercialCategoriesByCommercialSegmentId(
                        (
                            watchAllFields.mainDefinition
                                .commercialSegment as SelectElementModelFE
                        ).code as string
                    );
                const commercialCategoryList: CommercialCategorySelectFE[] =
                    fromGenericBeToGenericFe(commercialCategoriesData);
                setCommercialCategoryList(commercialCategoryList);
            }
        };

        getContent();
    }, [watchAllFields?.mainDefinition?.commercialSegment]);

    const handleChangeCommercialSegment = async (value: SelectElementModelFE) => {
        if (value) {
            form.setValue("mainDefinition.commercialSegment", value);
            const commercialCategoriesData =
                await getCommercialCategoriesByCommercialSegmentId(
                    (value as SelectElementModelFE).code as string
                );
            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            form.setValue("mainDefinition.commercialCategory", null);
            setCommercialCategoryList(commercialCategoryList);
        } else {
            form.setValue("mainDefinition.commercialSegment", value);
            // TODO: togliere COMMERCIAL_CATEGORIES || quando si pusha
            // const commercialCategoriesData: CommercialCategoryEntityResponse[] = COMMERCIAL_CATEGORIES || await getCommercialCategories();
            const commercialCategoriesData: CommercialCategoryEntityResponse[] =
                await getCommercialCategories();

            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            form.setValue("mainDefinition.commercialCategory", null);
            setCommercialCategoryList(commercialCategoryList);
        }
    };

    return (
        <Grid container spacing={2} alignItems="start">
            {!readOnly && rateInUse && (
                <CustomBanner message={t("pricing:rate.banner")} severity={"warning"}/>
            )}
            <Grid item xs={12} sm={12} md={6}>
                <MainDefinitionComponent
                    dealer={{
                        readonly: readOnly || form.formState.isSubmitting,
                    }}
                    legalEntity={{
                        readonly: readOnly || form.formState.isSubmitting,
                    }}
                    commercialSegment={{
                        options: commercialSegmentList,
                        readonly: readOnly || form.formState.isSubmitting,
                        handleChange: handleChangeCommercialSegment,
                    }}
                    commercialCategory={{
                        options: commercialCategoryList,
                        readonly: readOnly || form.formState.isSubmitting,
                    }}
                    name={{
                        readonly: readOnly || form.formState.isSubmitting,
                        error: Boolean(form.formState.errors.mainDefinition?.name),
                    }}
                    description={{
                        readonly: readOnly || form.formState.isSubmitting,
                        error: Boolean(form.formState.errors.mainDefinition?.description),
                    }}
                    tpUnit={{
                        options: unitsEnum,
                        readonly: readOnly || form.formState.isSubmitting,
                    }}
                    carGroup={{
                        options: ["ACRISS", "SIPP"],
                        readonly: readOnly || form.formState.isSubmitting,
                    }}
                    reservationFrom={{
                        readonly: readOnly || form.formState.isSubmitting || rateInUse,
                        dateMinOrMax:
                            dayjs().diff(dayjs(rate?.reservationFrom), "minutes") > 0
                                ? rate?.reservationFrom
                                : undefined,
                    }}
                    reservationTo={{
                        readonly: readOnly || form.formState.isSubmitting,
                        dateMinOrMax:
                            watchAllFields.mainDefinition?.reservationFrom || undefined,
                    }}
                    pickupFrom={{
                        readonly: readOnly || form.formState.isSubmitting,
                        dateMinOrMax:
                            dayjs().diff(dayjs(rate?.pickupDateFrom), "minutes") > 0
                                ? rate?.pickupDateFrom
                                : undefined,
                    }}
                    pickupTo={{
                        readonly: readOnly || form.formState.isSubmitting,
                        dateMinOrMax:
                            watchAllFields.mainDefinition?.pickupFrom || undefined,
                    }}
                    tax={{
                        readonly: readOnly || form.formState.isSubmitting,
                    }}
                />
            </Grid>
            <Grid item container xs={12} sm={12} md={6} spacing={2}>
                <Grid item xs={12} sm={12} md={12}>
                    <OtherDefinitionComponent
                        currency={{
                            options: currencyList,
                            readonly: readOnly || form.formState.isSubmitting,
                        }}
                        dealer={{
                            options: providers,
                            readonly: readOnly || form.formState.isSubmitting,
                        }}
                        unlimitedKm={{
                            readonly:
                                readOnly || form.formState.isSubmitting || Boolean(rate?.id),
                            handleChange: (value: boolean) => {
                                form.setValue("otherDefinition.unlimitedKm", value);
                                if (value) {
                                    form.setValue("otherDefinition.includedKm", null);
                                }
                            },
                        }}
                        includedKm={{
                            readonly:
                                readOnly ||
                                form.formState.isSubmitting ||
                                watchAllFields.otherDefinition?.unlimitedKm ||
                                Boolean(rate?.id),
                        }}
                        publicEl={{
                            readonly: readOnly || form.formState.isSubmitting,
                        }}
                        minimumDuration={{
                            readonly: readOnly || form.formState.isSubmitting,
                        }}
                        maximumDuration={{
                            readonly: readOnly || form.formState.isSubmitting,
                        }}
                        lateTolerance={{
                            readonly: readOnly || form.formState.isSubmitting,
                        }}
                        lateDropOffRate={{
                            readonly: readOnly || form.formState.isSubmitting,
                            options: rateSelectList || [],
                        }}
                        bestPrice={{
                            readonly: readOnly || form.formState.isSubmitting,
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                    <ReferenceTariffComponent
                        enable={{
                            readonly:
                                readOnly || form.formState.isSubmitting || Boolean(rate?.id),
                            handleChange: (value: boolean) => {
                                form.setValue("referenceTariff.enable", value);
                                if (!value) {
                                    form.setValue("referenceTariff.rate", null);
                                    form.setValue(
                                        "referenceTariff.discountIncreasePercentage",
                                        null
                                    );
                                    form.setValue("referenceTariff.discountIncreaseFixed", null);
                                }
                            },
                        }}
                        rate={{
                            readonly:
                                readOnly ||
                                form.formState.isSubmitting ||
                                !watchAllFields.referenceTariff?.enable ||
                                Boolean(rate?.id),
                            options: rateSelectList,
                            handleChange: (value: RateSelectFE) => {
                                form.setValue("referenceTariff.rate", value);
                                if (!value) {
                                    form.setValue(
                                        "referenceTariff.discountIncreasePercentage",
                                        ""
                                    );
                                    form.setValue("referenceTariff.discountIncreaseFixed", "");
                                }
                            },
                        }}
                        discountIncreasePercentage={{
                            readonly: readOnly || form.formState.isSubmitting,
                        }}
                        discountIncreaseFixed={{
                            readonly: readOnly || form.formState.isSubmitting,
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                    <AdvancedBookingComponent
                        threshold={{
                            readonly: readOnly || form.formState.isSubmitting,
                        }}
                        discountRate={{
                            readonly: readOnly || form.formState.isSubmitting,
                        }}
                    />
                </Grid>
            </Grid>

            {!readOnly &&
                <Grid container item xs={12} justifyContent="flex-end" columnGap={2}>
                    <Grid>
                        <Button variant="outlined"
                                sx={{marginRight: "15px"}}
                                color={"accent"}
                                type={"button"}
                                onClick={() => {
                                    window.close()
                                }}
                        >
                            {t("pricing:actions.cancel")}
                        </Button>
                        <Button color={"accent"}
                                type={"button"}
                                onClick={handleSubmit}
                                sx={{border: `1px solid ${theme.palette.accent.main}`}}
                        >
                            {t("pricing:actions.confirm")}
                        </Button>
                    </Grid>
                </Grid>
            }
        </Grid>
    );
};

export default RateBaseDefinition;
